import type { PlayerStatsRow } from '@api-service/player-stats';
import type {
  SpiderChartAxis,
  SpiderChartSeries
} from '@components/spider-chart';
import {
  DEFENSIVE_CONTRIBUTION_FROM_SEASON,
  EXPECTED_POINTS_FROM_SEASON,
  LOWER_IS_BETTER_STAT_PATHS
} from '@lib/page-utils/player-profile/metrics';

export type ComparisonSpiderAxis = SpiderChartAxis & {
  statPath: string;
  /**
   * First season the API carries a real value. Earlier seasons return 0 for
   * everyone rather than null, so the axis is dropped rather than drawn pinned
   * to the centre.
   */
  availableFromSeason?: number;
};

const POINTS_AXIS: ComparisonSpiderAxis = {
  key: 'points',
  label: 'Points',
  statPath: 'fpl.points'
};
const EXPECTED_POINTS_AXIS: ComparisonSpiderAxis = {
  key: 'xpts',
  label: 'xPts',
  statPath: 'fpl.expectedPoints',
  availableFromSeason: EXPECTED_POINTS_FROM_SEASON
};
const GOALS_AXIS: ComparisonSpiderAxis = {
  key: 'goals',
  label: 'Goals',
  statPath: 'shooting.goals.total'
};
const EXPECTED_GOALS_AXIS: ComparisonSpiderAxis = {
  key: 'xg',
  label: 'xG',
  statPath: 'shooting.expectedGoals'
};
const EXPECTED_ASSISTS_AXIS: ComparisonSpiderAxis = {
  key: 'xa',
  label: 'xA',
  statPath: 'creativity.expectedAssists'
};
const ASSISTS_AXIS: ComparisonSpiderAxis = {
  key: 'assists',
  label: 'Assists',
  statPath: 'creativity.assists'
};
const CLEAN_SHEETS_AXIS: ComparisonSpiderAxis = {
  key: 'cleanSheets',
  label: 'Clean sheets',
  statPath: 'defending.cleanSheets'
};
const EXPECTED_GOALS_CONCEDED_AXIS: ComparisonSpiderAxis = {
  key: 'xgc',
  label: 'xGC',
  statPath: 'defending.expectedGoalsConceded'
};
const SAVES_AXIS: ComparisonSpiderAxis = {
  key: 'saves',
  label: 'Saves',
  statPath: 'fpl.saves'
};
const BONUS_AXIS: ComparisonSpiderAxis = {
  key: 'bonus',
  label: 'Bonus',
  statPath: 'fpl.bonus'
};
const DEFENSIVE_CONTRIBUTION_AXIS: ComparisonSpiderAxis = {
  key: 'defcon',
  label: 'Def con',
  statPath: 'fpl.defensiveContribution.points',
  availableFromSeason: DEFENSIVE_CONTRIBUTION_FROM_SEASON
};

/**
 * The axes when everyone on the chart is in the same position, in the order
 * they go round the chart. Attacking output sits on one side and creativity or
 * defending on the other, so the lean of a shape reads as "scorer" versus
 * "creator" or "attacking" versus "defensive". Points and xPts mirror the
 * head-to-head tables so the chart and the table cannot disagree on the
 * headline.
 */
const AXES_BY_POSITION: Record<string, ComparisonSpiderAxis[]> = {
  GK: [
    POINTS_AXIS,
    CLEAN_SHEETS_AXIS,
    SAVES_AXIS,
    EXPECTED_POINTS_AXIS,
    BONUS_AXIS,
    EXPECTED_GOALS_CONCEDED_AXIS
  ],
  DEF: [
    POINTS_AXIS,
    CLEAN_SHEETS_AXIS,
    DEFENSIVE_CONTRIBUTION_AXIS,
    EXPECTED_POINTS_AXIS,
    EXPECTED_GOALS_AXIS,
    EXPECTED_GOALS_CONCEDED_AXIS
  ],
  MID: [
    POINTS_AXIS,
    GOALS_AXIS,
    EXPECTED_GOALS_AXIS,
    EXPECTED_POINTS_AXIS,
    EXPECTED_ASSISTS_AXIS,
    ASSISTS_AXIS
  ],
  FWD: [
    POINTS_AXIS,
    GOALS_AXIS,
    EXPECTED_GOALS_AXIS,
    EXPECTED_POINTS_AXIS,
    EXPECTED_ASSISTS_AXIS,
    ASSISTS_AXIS
  ]
};

/** The axes for a mixed-position chart: the six the tables show for everyone. */
export const COMPARISON_SPIDER_AXES: ComparisonSpiderAxis[] =
  AXES_BY_POSITION.MID;

/** Field groups the stats endpoint needs to serve every axis above. */
export const COMPARISON_SPIDER_FIELDS = 'fpl,shooting,creativity,defending';

/**
 * The axes to draw for the players on the chart. A single shared position gets
 * that position's set, because goals and assists say nothing about a
 * goalkeeper; a mixed selection falls back to the shared six. Axes whose stat
 * did not exist in the selected season are dropped.
 */
export const getComparisonSpiderAxes = (
  positions: string[],
  season: number | null
): ComparisonSpiderAxis[] => {
  const distinct = [...new Set(positions)];
  const axes =
    distinct.length === 1
      ? AXES_BY_POSITION[distinct[0]] ?? COMPARISON_SPIDER_AXES
      : COMPARISON_SPIDER_AXES;
  return axes.filter(
    (axis) =>
      season === null ||
      axis.availableFromSeason === undefined ||
      season >= axis.availableFromSeason
  );
};

const RAW_VALUE_DECIMAL_PLACES = 1;

/**
 * Formats a raw stat for the tooltip. Whole numbers stay whole, because
 * "7.0 assists" reads as a measurement error rather than seven assists.
 */
const formatRawValue = (value: number | null): string | null => {
  if (value === null) return null;
  return Number.isInteger(value)
    ? value.toString()
    : value.toFixed(RAW_VALUE_DECIMAL_PLACES);
};

const readStatPath = (
  row: PlayerStatsRow | undefined,
  path: string
): number | null => {
  if (!row) return null;
  let cursor: unknown = row;
  for (const segment of path.split('.')) {
    if (cursor && typeof cursor === 'object' && segment in cursor) {
      cursor = (cursor as Record<string, unknown>)[segment];
    } else {
      return null;
    }
  }
  return typeof cursor === 'number' ? cursor : null;
};

/**
 * Sums a stat across the players on one side. A side with no data at all for a
 * path returns null rather than 0, so the chart can leave a gap instead of
 * drawing the side as worst-in-class on a metric nobody measured.
 */
export const sumSideStat = (
  rows: Array<PlayerStatsRow | undefined>,
  path: string
): number | null => {
  const values = rows
    .map((row) => readStatPath(row, path))
    .filter((value): value is number => value !== null);
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0);
};

export type SpiderSideInput = {
  key: string;
  label: string;
  color: string;
  rows: Array<PlayerStatsRow | undefined>;
  /** Positions of the players on this side, used to pick the axis range. */
  positions: string[];
};

/** The values that read as 0 and 100 on an axis for one position. */
export type StatRange = { low: number; high: number };

export type PositionRangeLookup = (
  position: string,
  statPath: string
) => StatRange | null;

export const RANGE_LOW_PERCENTILE = 0.05;
export const RANGE_HIGH_PERCENTILE = 0.95;

/** Nearest-rank percentile of a sorted ascending list. */
const percentileOf = (sortedValues: number[], percentile: number): number =>
  sortedValues[Math.floor(percentile * (sortedValues.length - 1))];

/**
 * The 5th-to-95th percentile range of a stat across a population, which is
 * the scale each axis is drawn on. Trimming the tails means one outlier at the
 * top does not shrink everyone else, and the handful of near-zero part-timers
 * at the bottom do not stretch the centre. Null when the population is too
 * small or flat to define a range.
 */
export const buildStatRange = (values: number[]): StatRange | null => {
  if (values.length < 2) return null;
  const sorted = [...values].sort((first, second) => first - second);
  const low = percentileOf(sorted, RANGE_LOW_PERCENTILE);
  const high = percentileOf(sorted, RANGE_HIGH_PERCENTILE);
  return high > low ? { low, high } : null;
};

const SCALE_MAXIMUM = 100;

const clampToScale = (value: number): number =>
  Math.min(SCALE_MAXIMUM, Math.max(0, value));

/**
 * A side made of several players is summed, so its range is the widest across
 * the positions on it; the sum can still clear the top and is clipped there.
 */
const resolveSideRange = (
  positions: string[],
  statPath: string,
  getRange: PositionRangeLookup
): StatRange | null => {
  const ranges = positions
    .map((position) => getRange(position, statPath))
    .filter((range): range is StatRange => range !== null);
  if (ranges.length === 0) return null;
  return {
    low: Math.min(...ranges.map((range) => range.low)),
    high: Math.max(...ranges.map((range) => range.high))
  };
};

/** Where `value` falls on the range, inverted for stats where lower is better. */
const scaleOnRange = (
  value: number,
  range: StatRange,
  isLowerBetter: boolean
): number => {
  const share = (value - range.low) / (range.high - range.low);
  return clampToScale((isLowerBetter ? 1 - share : share) * SCALE_MAXIMUM);
};

/**
 * Until the position range has loaded, the axis runs to the higher side so the
 * chart draws a shape rather than nothing. Only ever a transient state.
 */
const scaleAgainstBestSide = (
  value: number,
  sideValues: Array<number | null>
): number => {
  const best = Math.max(
    ...sideValues
      .filter((entry): entry is number => entry !== null)
      .map(Math.abs)
  );
  return best === 0 ? 0 : (value / best) * SCALE_MAXIMUM;
};

/**
 * Builds the chart series for the comparison tool. Every axis is a fixed
 * linear scale on the raw stat, running from the 5th to the 95th percentile of
 * the player's position over the same filters, so the edge always means
 * "elite for the position" and the centre "bottom of the pack", and the gap
 * between two players is drawn at its true size.
 *
 * Three other scales were tried on real players and rejected. Percentile rank
 * collapsed two premium forwards onto the outer ring together, because both
 * rank in the top few percent on everything. Share of the position's best drew
 * an honest but tiny shape for anyone short of the league leader. Relative to
 * the two players made whoever led a full hexagon on every axis, which the
 * table already says.
 */
export const buildComparisonSpiderSeries = (
  sides: SpiderSideInput[],
  getRange?: PositionRangeLookup,
  axes: ComparisonSpiderAxis[] = COMPARISON_SPIDER_AXES
): SpiderChartSeries[] => {
  const rawByAxis = new Map<string, Array<number | null>>(
    axes.map((axis) => [
      axis.key,
      sides.map((side) => sumSideStat(side.rows, axis.statPath))
    ])
  );

  return sides.map((side, sideIndex) => {
    const values: Record<string, number | null> = {};
    const rawValues: Record<string, string | null> = {};

    for (const axis of axes) {
      const sideValues = rawByAxis.get(axis.key) ?? [];
      const raw = sideValues[sideIndex] ?? null;
      rawValues[axis.key] = formatRawValue(raw);

      if (raw === null) {
        values[axis.key] = null;
        continue;
      }
      const range = getRange
        ? resolveSideRange(side.positions, axis.statPath, getRange)
        : null;
      values[axis.key] = range
        ? scaleOnRange(
            raw,
            range,
            LOWER_IS_BETTER_STAT_PATHS.has(axis.statPath)
          )
        : scaleAgainstBestSide(raw, sideValues);
    }

    return {
      key: side.key,
      label: side.label,
      color: side.color,
      values,
      rawValues
    };
  });
};

/** Caption explaining the scale for the positions on the chart. */
export const buildSpiderScaleCaption = (positions: string[]): string => {
  const distinct = [...new Set(positions)];
  const subject =
    distinct.length === 1
      ? `a bottom-of-the-pack ${distinct[0]} to an elite one`
      : 'a bottom-of-the-pack player to an elite one in their own position';
  return `Each axis runs from ${subject} (5th to 95th percentile) over the selected gameweeks.`;
};

export type SelectedPlayerIdentity = { optaId: string; displayName: string };

/**
 * Selected players the stats response returned no row for. Player ids are
 * re-minted each season, so the match is on the stable Opta id, which the
 * stats row carries as `optaUuid`. In practice this is a player who was not in
 * the league in the selected season.
 */
export const findPlayersWithoutRows = (
  players: SelectedPlayerIdentity[],
  rows: PlayerStatsRow[]
): string[] => {
  const optaIdsWithRows = new Set(rows.map((row) => row.optaUuid));
  return players
    .filter((player) => !optaIdsWithRows.has(player.optaId))
    .map((player) => player.displayName);
};

const SEASON_LABEL_SUFFIX_LENGTH = 2;

/** 2024 renders as "2024/25", matching the season filter's own labels. */
const formatSeasonLabel = (season: number): string =>
  `${season}/${(season + 1).toString().slice(-SEASON_LABEL_SUFFIX_LENGTH)}`;

const joinNames = (names: string[]): string => {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * Caption shown under the chart when a selected player has no data for the
 * season, so a one-sided radar is explained rather than left looking broken.
 */
export const buildMissingSeasonDataCaption = (
  missingNames: string[],
  season: number
): string | null => {
  if (missingNames.length === 0) return null;
  const verb = missingNames.length === 1 ? 'was' : 'were';
  return `No ${formatSeasonLabel(season)} data for ${joinNames(
    missingNames
  )}, who ${verb} not in the league that season.`;
};
